import "./deposits.scss";

import { Roles } from "../../../utils/lists";
import { ChevronsDown, Edit3Icon } from "lucide-react";

import AdminModalDepost from "../../../components/admin-modal-deposit";

import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { fetchClientsByOrganization, getAllUsers, getDepositorsByOrganization, getUserInfo, UserInfo } from "../../../store/UserSlice";
import { getOrganizations } from "../../../store/OrganizationSlice";
import { fetchAssets, fetchBanks } from "../../../store/AssetsSlice";
import { fetchCreditsByUserId, fetchDepositsByType, setAddDepoositModalStatus } from "../../../store/DepositsSlice";
import { DepositType } from "../../../models/Depoist";
import { useParams } from "react-router-dom";
import EditCreditStatusModal from "../../../components/modal/modal-edit-credit-status/modal-edit-credit-status";

import { DataList, DataListColumn, StatusBadge, MoneyValue, PillTabs } from "../../../components/ui";

const enum DepositsType {
    DEPOSITS = "deposits",
    CREDITS  = "credits",
}

const AdminClientDeposits = () => {

    const dispatch = useAppDispatch();
    const { id }   = useParams();

    const userInfo: UserInfo = useAppSelector(getUserInfo);
    const deposits           = useAppSelector((s) => s.deposit.list);
    const credits            = useAppSelector((s) => s.deposit.creditsList);
    const modalStatus        = useAppSelector((s) => s.deposit.addDepositModalStatus);

    const [type, setType]             = useState<DepositsType>(DepositsType.DEPOSITS);
    const [editCredit, setEditCredit] = useState<any | null>(null);
    const isOwner = userInfo.role?.toLowerCase() === Roles.owner.toLowerCase();

    useEffect(() => {
        if (userInfo.role.toLowerCase() === Roles.owner.toLowerCase()) {
            dispatch(fetchClientsByOrganization());
            if (id) dispatch(fetchCreditsByUserId(+id));
            dispatch(getDepositorsByOrganization());
            dispatch(fetchDepositsByType(DepositType.OWNER));
        } else if (userInfo.role.toLowerCase() === Roles.superAdmin.toLowerCase()) {
            dispatch(fetchClientsByOrganization());
            dispatch(getDepositorsByOrganization());
            dispatch(fetchDepositsByType(DepositType.ALL));
            if (id) dispatch(fetchCreditsByUserId(+id));
        } else {
            if (userInfo.organizationUsers.length === 0) {
                return;
            }
            dispatch(fetchDepositsByType(DepositType.USER));
            // Nesta tela o cliente ve os proprios creditos: aqui nao ha `:id`
            // na rota, o alvo e ele mesmo. Sem este dispatch a aba Creditos
            // ficava no skeleton para sempre, porque `creditsList` seguia null.
            dispatch(fetchCreditsByUserId(userInfo.id));
        }
    }, [userInfo]);

    useEffect(() => {
        dispatch(fetchBanks());
        dispatch(fetchAssets());
        if (userInfo.role === Roles.superAdmin || userInfo.role === Roles.owner) {
            dispatch(getOrganizations());
            dispatch(getAllUsers());
        }
    }, [dispatch]);

    // A listagem chega em dois formatos: raw (rota /organization/:id, com os
    // aliases assetName/assetSymbol/assetType) e entidade (demais perfis, com a
    // relation `asset` carregada). Esta funcao normaliza os dois.
    const assetOf = (d: any) => ({
        name:   typeof d.asset === "string" ? d.asset : d.asset?.name,
        symbol: d.assetSymbol ?? d.asset?.symbol ?? d.symbol,
        isCrypto: (d.assetType ?? d.asset?.type ?? d.type) === "CRYPTO",
    });

    // Hash on-chain e longo demais para a celula: encurta e deixa o valor
    // completo no title para consulta/copia.
    const shortHash = (h?: string) =>
        !h ? "" : h.length <= 18 ? h : `${h.slice(0, 10)}...${h.slice(-6)}`;

    const depositsColumns: DataListColumn<any>[] = [
        {
            key: "currency", header: "Currency",
            render: (d) => (
                <span className="cell-stack">
                    <strong>{assetOf(d).name}</strong>
                    <small className="num">{d.trx_id}</small>
                </span>
            ),
        },
        { key: "org",        header: "Empresa",     render: (d) => d.organizationName },
        {
            key: "bank", header: "Banco / Hash",
            // Deposito cripto nao tem banco: mostra a hash da transacao.
            render: (d) => {
                const a = assetOf(d);
                if (!a.isCrypto) return d.bankName ?? d.bank?.name ?? "-";
                return d.hash
                    ? <span className="num" title={d.hash}>{shortHash(d.hash)}</span>
                    : <span style={{ opacity: .45 }}>-</span>;
            },
        },
        { key: "client",     header: "Cliente",     render: (d) => d.userFromWalletName },
        { key: "depositor",  header: "Depositante", render: (d) => <span style={{ opacity: .45 }}>{d.userName}</span> },
        { key: "type",       header: "Tipo",        render: (d) => <span style={{ opacity: .45 }}>{d.type}</span> },
        { key: "date",       header: "Data",        render: (d) => d.date, mono: true },
        {
            key: "amount", header: "Valor", align: "right",
            // Fiat: 2 casas fixas. Cripto: no minimo 6, ate 8.
            render: (d) => {
                const a = assetOf(d);
                return (
                    <MoneyValue
                        amount={d.value}
                        symbol={a.symbol}
                        precision={a.isCrypto ? 8 : 2}
                        minPrecision={a.isCrypto ? 6 : 2}
                        // tone neutro para a cripto nao levar tambem o verde do fiat
                        tone={a.isCrypto ? "neutral" : "up"}
                        className={a.isCrypto ? "delta-crypto" : ""}
                    />
                );
            },
        },
        { key: "status",     header: "Status",      render: (d) => <StatusBadge status={d.status} tone="success" /> },
    ];

    const creditsColumns: DataListColumn<any>[] = [
        {
            key: "currency", header: "Currency",
            render: (c) => (
                <span className="cell-stack">
                    <strong>{c.asset?.name}</strong>
                    <small className="num">{c.trx_id}</small>
                </span>
            ),
        },
        { key: "origem",  header: "Origem",  render: (c) => c.creator?.name },
        { key: "cliente", header: "Cliente", render: (c) => c.user?.name },
        { key: "tipo",    header: "Tipo",    render: () => "Credito" },
        { key: "data",    header: "Data",    render: (c) => c.createdAt, mono: true },
        { key: "amount",  header: "Valor",   render: (c) => <MoneyValue amount={c.value} tone="up" />, align: "right" },
        { key: "status",  header: "Status",  render: (c) => <StatusBadge status={c.status} /> },
        // Alterar status do credito e exclusivo do owner (o backend tambem bloqueia).
        ...(isOwner ? [{
            key: "actions", header: "", width: "50px", align: "center" as const,
            render: (c: any) => (
                <span
                    role="button"
                    className="edit-btn"
                    onClick={(e) => { e.stopPropagation(); setEditCredit(c); }}
                >
                    <Edit3Icon size={18} />
                </span>
            ),
        }] : []),
    ];

    return (
        <>
            <div className="admin-client-deposits-wrapper">

                <div className="flex sb row middle">
                    <h1 className="page-title">
                        Listar Depositos
                        <small>lista de todos os depositos realizados pelos clientes na plataforma.</small>
                    </h1>
                    <div className="buttons">
                        {userInfo.role.toLowerCase() === Roles.owner.toLowerCase() && (
                            <button
                                className="btn-pill primary"
                                onClick={() => dispatch(setAddDepoositModalStatus(!modalStatus))}
                            >
                                Adicionar Deposito
                            </button>
                        )}
                    </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                    <PillTabs
                        ariaLabel="Tipo de listagem"
                        value={type}
                        onChange={(k: string) => setType(k as DepositsType)}
                        tabs={[
                            { key: DepositsType.DEPOSITS, label: "Depositos" },
                            { key: DepositsType.CREDITS,  label: "Creditos" },
                        ]}
                    />
                </div>

                {type === DepositsType.DEPOSITS && (
                    <DataList
                        columns={depositsColumns}
                        rows={deposits ?? []}
                        loading={userInfo.organizationUsers.length > 0 ? !deposits : false}
                        empty= {
                            userInfo.organizationUsers.length > 0
                            ? "Nenhum depósito encontrado."
                            : "Usuário não vinculado a nenhuma empresa."
                        }
                        
                    />
                )}

                {type === DepositsType.CREDITS && (
                    <DataList
                        columns={creditsColumns}
                        rows={credits ?? []}
                        loading={!credits}
                        empty="Nenhum credito encontrado."
                    />
                )}

                <div className="buttons" style={{ marginTop: 20, justifyContent: "center" }}>
                    <button className="btn-pill ghost">
                        <ChevronsDown size={16} /> carregar mais <ChevronsDown size={16} />
                    </button>
                </div>

                {modalStatus && <AdminModalDepost />}

                {editCredit && (
                    <EditCreditStatusModal
                        credit={editCredit}
                        onClose={() => setEditCredit(null)}
                        onSuccess={() => { if (id) dispatch(fetchCreditsByUserId(+id)); }}
                    />
                )}
            </div>
        </>
    );
};

export default AdminClientDeposits;
